import type { ComponentProps, FormEventHandler } from 'react'
import { useCallback } from 'react'
import { formToObject } from 'lib/forms/formToObject'
import { createRequestAgentCallback } from './CreateRequestAgentCallback'
import RequestAgentCallbackFields from './RequestAgentCallbackFields'
import type { MappedRequestAgentCallbackResult, RequestAgentCallbackResult } from './requestAgentCallbackTypes'

interface Props extends ComponentProps<typeof RequestAgentCallbackFields> {
  chatId?: string
  formId?: string
  onSubmittingChange: (isSubmitting: boolean) => void
  onSubmitted: (results: MappedRequestAgentCallbackResult) => void | Promise<void>
  onDuplicateCallback: (results: MappedRequestAgentCallbackResult) => void
  onError: () => void
}

function RequestAgentCallbackForm(props: Props) {
  const {
    chatId,
    formId,
    requester,
    onSubmittingChange,
    onSubmitted,
    onDuplicateCallback,
    onError,
    ...fieldProps
  } = props

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()
    onSubmittingChange(true)

    const requestAgentCallbackFormData = formToObject<RequestAgentCallbackResult>(e.currentTarget)

    try {
      const results = await createRequestAgentCallback(requestAgentCallbackFormData, requester, chatId)
      if (results.customerHasRecentlyScheduledCallback) {
        onDuplicateCallback(results)
      } else {
        await onSubmitted(results)
      }
    } catch {
      onError()
    } finally {
      onSubmittingChange(false)
    }
  }, [chatId, onDuplicateCallback, onError, onSubmitted, onSubmittingChange, requester])

  return (
    <form id={formId} onSubmit={handleSubmit}>
      <RequestAgentCallbackFields
        {...fieldProps}
        requester={requester}
      />
    </form>
  )
}

export default RequestAgentCallbackForm
